/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { FormDefinitionWrapper } from './form/services/form';
import { Button, createStyles, Fab, Hidden, Menu, MenuItem, Theme } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { makeStyles } from "@material-ui/styles";
import React, { FC, useState, useEffect, } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { AppState, createEval, FormDescriptor } from "../../redux";
import { isProduction } from "../../utils/runtime";
import { useHistory } from "react-router";
import { EvaluationsClaim, CertificationsClaim } from '../../utils/auth';

export function getFullFormVersion(definition: FormDefinitionWrapper | FormDescriptor) {
  return `v${definition.version_program || 1}.${definition.version_major}.${definition.version_minor}`;
}

export const selectForms = createSelector(
  (state: AppState) => state.forms.items, items => items
);
export const selectCertifications = (state: AppState) => state.session.dfdmToken?.certifications ?? state.session.dfdmToken?.evaluations;
const selectCurrentUserFetched = createSelector(
  (state: AppState) => state.session.currentUserFetched, ready => ready
)
// one of these two selectors is getting messed up.
const selectFormsStale = createSelector(
  (state: AppState) => state.forms, forms => forms.stale
);
const selectFormsFetching = createSelector(
  (state: AppState) => state.forms, forms => {
    return forms.inFlightCount !== 0;
  }
);

/**
 * Form types in that list can not be created through the "New eval" button.
 * For example, ACC audits must be created in the portal (so they are associated with a facility), then are edited in the eval app.
 */
const formTypeBlacklist = [ 'wmp_audit' ];

const NewEvalButton: FC<{}> = props => {
  const classes = useStyles(props);

  // todo custom memoize (by uuid);
  const forms = useSelector(selectForms);
  const certifications: EvaluationsClaim | CertificationsClaim | undefined = useSelector(selectCertifications);
  /**
   * The current user must be local storage for the importDefaults hook on eval create.
   */
  const currentUserFetched = useSelector(selectCurrentUserFetched);
  /**
   * Only allow an eval to be created if the latest definitions have been fetched.
   */
  const formsStale = useSelector(selectFormsStale);
  /**
   * Only allow an eval to be created if no definition requests are currenly in-flight.
   */
  const formsFetching = useSelector(selectFormsFetching);

  const [ creatableForms, setCreatableForms ] = useState<FormDefinitionWrapper[]>([]);

  useEffect(() => {
      const f = Object.keys(forms)
        .filter(formType => {
          if (!certifications) {
            return false;
          }
          if(formTypeBlacklist.includes(formType)) {
            return false;
          };
          // backwards compatibility of token claims
          if (certifications[formType]) {
            if (Array.isArray(certifications[formType])) {
              if ((certifications[formType] as unknown as string[]).findIndex(status => status !== 'expired') >= 0) {
                return true;
              }
            } else if ((certifications[formType] as any)?.allow_create) {
              return true;
            }
          }
          return false;
        })
        .map(formType => {
          const defs = Object.values(forms[formType])
            .sort((a, b) => b.version_major - a.version_major)
            .filter(def => def.state === "published" || (!isProduction ? def.state === "testing" : false));
          return defs.length === 0 ? null : defs[0];
        })
        .filter(def => def !== null)
        .sort((a, b) => a!.title.localeCompare(b!.title)) as FormDefinitionWrapper[];

      setCreatableForms(f);
    },
    [forms, certifications]
  );

  const [disableNewEvalButton, setDisableNewEvalButton] = useState(true);

  useEffect(() => {
    setDisableNewEvalButton(!currentUserFetched || formsStale || formsFetching)
  }, [currentUserFetched, formsStale, formsFetching])

  const [anchorElement, setAnchorElement] = useState<Element | null>(null);

  const dispatch = useDispatch();
  const history = useHistory();

  function handleClose() {
    setAnchorElement(null);
  }
  
  function handleCreate(type: string) {
    handleClose();
    dispatch(createEval(type, history));
  }

  return (
    <>
      <Hidden xsDown>
        <Button
          aria-controls="new-menu"
          aria-haspopup="true"
          variant="contained"
          color="secondary"
          className={classes.newEvalButton}
          disabled={disableNewEvalButton}
          onClick={event => {
            if (creatableForms.length === 1) {
              handleCreate(creatableForms[0].form_id);
            } else {
              setAnchorElement(event.currentTarget);
            }
          }}
        >
          New Eval
        </Button>
      </Hidden>

      <Menu id="new-menu" anchorEl={anchorElement} keepMounted open={Boolean(anchorElement)} onClose={handleClose}>
        {creatableForms
          .sort((a, b) => a.title.localeCompare(b.title))
          .map(def => {
            return (
              <MenuItem key={def.id} onClick={() => handleCreate(def.form_id)}>
                <span><b>{def.short_title}</b> {getFullFormVersion(def)}</span>
              </MenuItem>
            );
          })}
      </Menu>

      <Hidden smUp>
        <Fab
          aria-controls="new-menu"
          aria-haspopup="true"
          color="secondary"
          className={classes.fabNewEvalButton}
          disabled={disableNewEvalButton}
          onClick={event => {
            if (creatableForms.length === 1) {
              handleCreate(creatableForms[0].form_id);
            } else {
              setAnchorElement(event.currentTarget);
            }
          }}
        >
          <AddIcon />
        </Fab>
      </Hidden>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    newEvalButton: {},
    fabNewEvalButton: {
      position: "fixed",
      bottom: theme.spacing(4),
      right: theme.spacing(4),
      zIndex: 1000,
    },
  })
);

export default NewEvalButton;
